/**
 * Utility to pre-warm Twitter card cache for new certificates
 */

/**
 * Pre-warm Twitter card cache for a new certificate
 * Call this immediately after a certificate is minted
 * @param {string} certificateId - The new certificate ID
 * @returns {Promise<boolean>} - Success status
 */
export async function prewarmTwitterCard(certificateId) {
  try {
    console.log('Pre-warming Twitter card for certificate:', certificateId);

    const prewarmUrl = `https://us-central1-atlantis-new-10-09-2025.cloudfunctions.net/prewarmTwitterCardHttp?certificateId=${encodeURIComponent(certificateId)}`;

    const response = await fetch(prewarmUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const result = await response.json();
      console.log('Twitter card pre-warming successful:', result);
      return true;
    } else {
      console.error('Twitter card pre-warming failed:', response.status, response.statusText);
      return false;
    }

  } catch (error) {
    console.error('Error pre-warming Twitter card:', error);
    return false;
  }
}

/**
 * Pre-warm Twitter card with retry logic
 * @param {string} certificateId - The new certificate ID
 * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
 * @returns {Promise<boolean>} - Success status
 */
export async function prewarmTwitterCardWithRetry(certificateId, maxRetries = 3) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    console.log(`Pre-warming attempt ${attempt}/${maxRetries} for certificate:`, certificateId);

    const success = await prewarmTwitterCard(certificateId);

    if (success) {
      console.log('Twitter card pre-warming successful on attempt:', attempt);
      return true;
    }

    if (attempt < maxRetries) {
      // Wait before retrying (exponential backoff)
      const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
      console.log(`Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  console.error('Twitter card pre-warming failed after all attempts');
  return false;
}

/**
 * Pre-warm multiple social media platforms
 * @param {string} certificateId - The new certificate ID
 * @returns {Promise<Object>} - Results for each platform
 */
export async function prewarmAllPlatforms(certificateId) {
  const certificateUrl = `https://app.impactfoundry.xyz/profile/gallery/projectic/${certificateId}`;

  const platforms = [
    { name: 'Twitter', userAgent: 'Twitterbot/1.0' },
    { name: 'Facebook', userAgent: 'facebookexternalhit/1.1' },
    { name: 'WhatsApp', userAgent: 'WhatsApp/2.0' },
    { name: 'LinkedIn', userAgent: 'LinkedInBot/1.0' }
  ];

  const results = {};

  for (const platform of platforms) {
    try {
      console.log(`Pre-warming ${platform.name} for certificate:`, certificateId);

      // Make a request with the platform's user agent
      const response = await fetch(certificateUrl, {
        headers: {
          'User-Agent': platform.userAgent,
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
        }
      });

      results[platform.name] = {
        success: response.ok,
        status: response.status,
        statusText: response.statusText
      };

      console.log(`${platform.name} pre-warming result:`, results[platform.name]);

    } catch (error) {
      results[platform.name] = {
        success: false,
        error: error.message
      };
      console.error(`${platform.name} pre-warming failed:`, error);
    }
  }

  return results;
}
